import { useCallback, useContext, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import { CurrentDataContext } from 'src/app/current-data';
import authRequest from 'src/utils/fetch';

import messages from '../i18n/use-code-reviewer-setting.i18n';
import { parseStringBool } from '../utils';

import { INTERNAL_WORKSPACE_UUIDS } from './constants';
import {
  CodeReviewerSetting,
  CodeReviewerSettingContainerType as ContainerType,
  CodeReviewerSettingContainerTypeATI,
} from './types';

const url = (method: 'get' | 'set') =>
  `/gateway/api/devai-core/public/v2/settings/${method}`;

const isSupportedInternalWorkspaceUUID = (uuid?: string): boolean =>
  INTERNAL_WORKSPACE_UUIDS.includes(uuid ?? '');

type IncompleteBodySetting = {
  containerType: CodeReviewerSettingContainerTypeATI;
  containerId: string;
  key: 'autoreview_settings';
};
type CodeReviewerBodySetting = IncompleteBodySetting & {
  value:
    | {
        autoreview_enabled?: boolean; // this is for the repo setting
      }
    | {
        autoreview_activated?: boolean; // this is for the workspace setting
      };
};

type CodeReviewerSettingApiResponse = {
  success: boolean;
  settings: {
    containerType: CodeReviewerSettingContainerTypeATI;
    containerId: string;
    key: 'autoreview_settings';
    value: {
      value:
        | {
            autoreview_enabled: string | boolean; // this is from the repo setting
          }
        | {
            autoreview_activated: string | boolean; // this is from the workspace setting
          };
      lastUpdatedTime: Date;
    };
  }[];
};

const getValue = (containerType: ContainerType, newValue: boolean) => {
  switch (containerType) {
    case ContainerType.Workspace:
      return { autoreview_activated: newValue };
    case ContainerType.Repository:
      return { autoreview_enabled: newValue };
  }

  return {};
};

const body = (
  containerType: ContainerType,
  repoUrl: string,
  newValue?: boolean
) => {
  let baseSetting: IncompleteBodySetting | CodeReviewerBodySetting = {
    containerType: containerType.ati,
    containerId: repoUrl,
    key: 'autoreview_settings',
  };

  if (newValue !== undefined) {
    baseSetting = {
      ...baseSetting,
      value: getValue(containerType, newValue),
    };
  }

  return JSON.stringify({ settings: [baseSetting] });
};

const requestOptions = (
  containerType: ContainerType,
  containerUUID: string,
  newValue?: boolean
): RequestInit => {
  // this replace wouldn't work with a regex (maybe due to curly brace), so had to do a double replace instead
  const trimmedUUID = containerUUID.replace('{', '').replace('}', '');
  const ARI = `${containerType.ari}/${trimmedUUID}`;

  return {
    method: 'POST',
    body: body(containerType, ARI, newValue),
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };
};

const getContainerUUID = (
  containerType: ContainerType,
  repository?: BB.Repository,
  workspace?: BB.Workspace
): string | undefined => {
  switch (containerType) {
    case ContainerType.Repository:
      return repository?.uuid;
    case ContainerType.Workspace:
      return workspace?.uuid;
  }

  return undefined;
};

type Props = {
  containerType: ContainerType;
};
export const useCodeReviewerSetting = ({
  containerType,
}: Props): CodeReviewerSetting => {
  const [error, setError] = useState<FormattedMessage.MessageDescriptor | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEnabledAtRepository, setIsEnabledAtRepository] =
    useState<boolean>(false);
  const [isEnabledAtWorkspace, setIsEnabledAtWorkspace] =
    useState<boolean>(false);

  const { repository, workspace } = useContext(CurrentDataContext);

  const getCodeReviewerSetting = useCallback(
    async (selectedContainerType: ContainerType) => {
      setError(null);

      const containerUUID = getContainerUUID(
        selectedContainerType,
        repository,
        workspace
      );

      try {
        if (containerUUID) {
          const res = await fetch(
            authRequest(
              url('get'),
              requestOptions(selectedContainerType, containerUUID)
            )
          );
          if (!res.ok) {
            throw new Error(
              `http error status ${res.status} with text: ${res.statusText}`
            );
          }
          const data = (await res.json()) as CodeReviewerSettingApiResponse;

          const newValue =
            data.settings.length > 0
              ? parseStringBool(data.settings[0].value.value)
              : selectedContainerType === ContainerType.Repository
              ? isSupportedInternalWorkspaceUUID(workspace?.uuid) // Internal workspaces use opt in model rather than opt out
              : false;

          switch (selectedContainerType) {
            case ContainerType.Repository:
              setIsEnabledAtRepository(newValue);
              await getCodeReviewerSetting(ContainerType.Workspace);
              break;
            case ContainerType.Workspace:
              setIsEnabledAtWorkspace(newValue);
              break;
          }

          setIsLoading(false);
        } else {
          throw new Error('Container does not exist');
        }
      } catch {
        setError(messages.codeReviewerGenericGetError);
        setIsLoading(false);
      }
    },
    [repository, workspace]
  );

  useEffect(() => {
    getCodeReviewerSetting(containerType);
  }, [getCodeReviewerSetting, containerType]);

  const updateContainerValue = useCallback(
    async (newValue: boolean) => {
      const containerUUID = getContainerUUID(
        containerType,
        repository,
        workspace
      );

      if (containerUUID) {
        const res = await fetch(
          authRequest(
            url('set'),
            requestOptions(containerType, containerUUID, newValue)
          )
        );
        if (!res.ok) {
          throw new Error(
            `http error status ${res.status} with text: ${res.statusText}`
          );
        }

        // Update setting for non-optimistic update
        if (containerType === ContainerType.Workspace) {
          setIsEnabledAtWorkspace(newValue);
        }
      } else {
        throw new Error(`${containerType.name} does not exist`);
      }
    },
    [containerType, repository, workspace]
  );

  const updateRepositorySetting = useCallback(async () => {
    setError(null);
    const newValue = !isEnabledAtRepository;

    // Optimistically update
    setIsEnabledAtRepository(newValue);

    try {
      await updateContainerValue(newValue);
    } catch {
      setIsEnabledAtRepository(!newValue); // reverse optimistic update
      if (newValue) {
        setError(messages.codeReviewerRepositoryOnError);
      } else {
        setError(messages.codeReviewerRepositoryOffError);
      }
    }
  }, [isEnabledAtRepository, updateContainerValue]);

  const updateWorkspaceSetting = useCallback(async () => {
    setError(null);
    const newValue = !isEnabledAtWorkspace;

    // Optimistically update
    setIsEnabledAtWorkspace(newValue);

    try {
      await updateContainerValue(newValue);
    } catch {
      setIsEnabledAtWorkspace(!newValue); // reverse optimistic update
      if (newValue) {
        setError(messages.codeReviewerWorkspaceOnError);
      } else {
        setError(messages.codeReviewerWorkspaceOffError);
      }
    }
  }, [isEnabledAtWorkspace, updateContainerValue]);

  const updateContainerSetting = useCallback(async () => {
    switch (containerType) {
      case ContainerType.Workspace:
        await updateWorkspaceSetting();
        break;
      case ContainerType.Repository:
        await updateRepositorySetting();
        break;
    }
  }, [updateWorkspaceSetting, updateRepositorySetting, containerType]);

  return {
    error,
    isLoading,
    isEnabledAtRepository,
    isEnabledAtWorkspace,
    updateContainerSetting,
  };
};
